'use client';

import React, { useState, useEffect, useMemo } from 'react';
import { Editor, Frame, useNode } from '@craftjs/core';
import { captureException } from '@/lib/observability';

// =====================
// Componentes del builder
// =====================
import { AUTO_FIT_ROW, autoFitItem } from '@/components/builder2/utils/autoFitRow';
import { Container } from '@/components/builder2/userComponents/Container';
import { Section } from '@/components/builder2/userComponents/Section';
import { Grid } from '@/components/builder2/userComponents/Grid';
import { Text } from '@/components/builder2/userComponents/Text';
import { Image } from '@/components/builder2/userComponents/Image';
import { Button } from '@/components/builder2/userComponents/Button';
import { Heading } from '@/components/builder2/userComponents/Heading';
import { Divider } from '@/components/builder2/userComponents/Divider';
import { Spacer } from '@/components/builder2/userComponents/Spacer';
import { Icon } from '@/components/builder2/userComponents/Icon';
import { SocialLinks } from '@/components/builder2/userComponents/SocialLinks';
import { VideoPlayer } from '@/components/builder2/userComponents/VideoPlayer';
import { ImageText } from '@/components/builder2/userComponents/ImageText';
import { Card } from '@/components/builder2/userComponents/Card';
import { ListBlock } from '@/components/builder2/userComponents/ListBlock';
import { MapEmbed } from '@/components/builder2/userComponents/MapEmbed';
import { RenderNodoLibre } from '@/components/builder2/RenderNodoLibre';
import { PiezaLibre } from '@/components/builder2/utils/piezaLibre';
import { GalleryBlock } from '@/components/builder2/userComponents/GalleryBlock';
import { Carousel } from '@/components/builder2/userComponents/Carousel';
import { AccordionBlock } from '@/components/builder2/userComponents/AccordionBlock';
import { IconText } from '@/components/builder2/userComponents/IconText';
import { WhatsAppButton } from '@/components/builder2/userComponents/WhatsAppButton';
import {
  HeroBasic,
  HeroWithImage,
  HeroWithBackground,
  HeroWithCard,
  HeroCarSearch,
  HeroFeatureCards,
  HeroTestimonial,
  HeroSearchBanner,
  HeroImageDivided,
  HeroWithLogo,
  HeroWelcome,
  HeroWithVideoEmbed,
  HeroMega,
} from '@/components/builder2/sections/initialfold';
import { HeroMinimalistic } from '@/components/builder2/sections/initialfold/HeroMinimalistic';
import { VehicleGrid } from '@/components/builder2/sections/vehicles';
import { VehicleGrid2 } from '@/components/builder2/sections/vehicles/VehicleGrid2';
import { VehicleCarousel } from '@/components/builder2/sections/vehicles/VehicleCarousel';
import { TraditionalVehicleGrid } from '@/components/builder2/sections/vehicles/TraditionalVehicleGrid';
import { Testimonials } from '@/components/builder2/sections/testimonials';
import { FAQ, WhyChooseUs } from '@/components/builder2/sections/features';
import { TraditionalWhyUs } from '@/components/builder2/sections/features/TraditionalWhyUs';
import { VideoEmbed } from '@/components/builder2/sections/videos/VideoEmbed';
import { ContactCTA } from '@/components/builder2/sections/contact/ContactCTA';
import { TraditionalContactCTA as BuilderTraditionalContactCTA } from '@/components/builder2/sections/contact/TraditionalContactCTA';
import HowToArrive from '@/sections/home/HowToArrive';
import { TraditionalHowToArrive } from '@/components/builder2/sections/contact/TraditionalHowToArrive';
import { FloatingWhatsApp } from '@/components/builder2/sections/contact/FloatingWhatsApp';
import { EmbedCodeBlock } from '@/components/builder2/sections/embed/EmbedCodeBlock';
// Moderna sections
import { HeroModerno } from '@/components/builder2/sections/moderna/HeroModerno';
import { StatsModerno } from '@/components/builder2/sections/moderna/StatsModerno';
import { TestimonialsModerno } from '@/components/builder2/sections/moderna/TestimonialsModerno';
import { CTAModerno } from '@/components/builder2/sections/moderna/CTAModerno';
import { FooterModerno } from '@/components/builder2/sections/moderna/FooterModerno';
// Premium sections
import { HeroPremium } from '@/components/builder2/sections/premium/HeroPremium';
import { FeatureShowcase } from '@/components/builder2/sections/premium/FeatureShowcase';
import { TestimonialsPremium } from '@/components/builder2/sections/premium/TestimonialsPremium';
import { GalleryPremium } from '@/components/builder2/sections/premium/GalleryPremium';
import { CTAPremium } from '@/components/builder2/sections/premium/CTAPremium';
// Layout sections — rendered by builder, layout conditionally hides its own nav/footer
import { BuilderNavbar } from '@/components/builder2/sections/layout/BuilderNavbar';
import { NavbarSimple } from '@/components/builder2/sections/layout/NavbarSimple';
import { Footer } from '@/components/builder2/sections/layout/Footer';
import { StatsCounter } from '@/components/builder2/sections/marketing/StatsCounter';
import { PromoBanner } from '@/components/builder2/sections/marketing/PromoBanner';
import { AwardsBadges } from '@/components/builder2/sections/marketing/AwardsBadges';
import { TrustBadges } from '@/components/builder2/sections/marketing/TrustBadges';
import { LogoCloud } from '@/components/builder2/sections/marketing/LogoCloud';
import { LogoMarquee } from '@/components/builder2/sections/marketing/LogoMarquee';
import { LogoGrid } from '@/components/builder2/sections/marketing/LogoGrid';
import { PhotoGallery } from '@/components/builder2/sections/media/PhotoGallery';
import { ImageCarousel } from '@/components/builder2/sections/media/ImageCarousel';
import { TeamMembers } from '@/components/builder2/sections/team/TeamMembers';

// Form imports
//
// Estáticos a propósito. Se probó diferirlos con `next/dynamic` (−60 KB de JS
// en el home): el harness `test:contact-info` mostró que el embed deja de
// renderizar el formulario en el primer pintado —la sección aparece con la
// columna de información y la tarjeta del formulario vacía hasta que llega el
// chunk—, un parpadeo que hoy no existe. Se mantiene el comportamiento actual.
import FinancingForm from '@/components/forms/FinancingForm';
import ConsignmentsForm from '@/components/forms/ConsignmentsForm';
import BuyDirectForm, { PrecioModo } from '@/components/forms/BuyDirectForm';
import WeSearchForm from '@/components/forms/WeSearchForm';
import ContactForm from '@/components/forms/ContactForm';
import AgendarForm from '@/components/booking/AgendarForm';
import useClientStore from '@/store/useClientStore';
import { useTranslation } from '@/i18n/hooks/useTranslation';
import { isBrasil, forcedSiteLanguage } from '@/lib/country';
import { EMBEDS_US } from '@/lib/textos-usa';
import { isBlankHtml } from '@/utils/functions';
import { htmlSeguro } from '@/components/builder2/utils/htmlSeguro';
import { financingPt } from '@/i18n/locales/pt/financing';
import { consignmentsPt } from '@/i18n/locales/pt/consignments';
import { buyDirectPt } from '@/i18n/locales/pt/buyDirect';
import { weSearchForYouPt } from '@/i18n/locales/pt/weSearchForYou';
import { traducirDefaultsDelArbolPt } from '@/components/builder2/utils/defaultsPt';
import { completarDefaultsDelArbolEn } from '@/components/builder2/utils/defaultsEn';
import { supabase } from '@/lib/supabase';
import { Dealership } from '@/utils/types';
import OpeningHours from '@/sections/home/OpeningHours';
import { getDealershipHours, type PublicDayHours } from '@/lib/dealership-hours';
import { sucursalesDelCliente, horasDelCliente } from '@/lib/sucursales-del-store';
import { DEALERSHIP_PUBLIC_COLS } from '@/lib/website-config-cols';

// Hook: returns true when viewing in a non-default language (i.e. needs translations)
function useIsTranslated() {
  const { client } = useClientStore();
  const { currentLanguage } = useTranslation();
  const defaultLang = client?.default_language || 'es';
  // EE.UU. (inglés obligatorio por país): el contenido del builder se muestra
  // tal cual, nunca se reemplaza por los textos del i18n.
  if (forcedSiteLanguage(client?.country) === 'en') return false;
  return !!(client?.has_language_selector && currentLanguage !== defaultLang);
}

/**
 * ¿El tenant es de Brasil? Las listas fijas de los embeds de formularios
 * (beneficios, requisitos, pasos) salen del `embed` del i18n pt —las mismas que
 * ya usa el bloque SEO de cada página (`components/seo/*SeoContent`)— y NO por
 * `t('financing.embed…')`: esas claves tienen versión en inglés, y un sitio
 * chileno con selector en inglés (ERA) siempre vio estos textos en español.
 * Todo tenant que no es de Brasil sigue con los literales de siempre.
 */
function useEsBrasil() {
  const { client } = useClientStore();
  return isBrasil(client?.country);
}

/**
 * ¿El tenant es de EE.UU.? Las mismas listas fijas, en inglés (`EMBEDS_US`, las
 * mismas que emite el bloque SEO). La automotora chilena en inglés NO entra acá:
 * sigue con los literales en español.
 */
function useEsUsa() {
  const { client } = useClientStore();
  return forcedSiteLanguage(client?.country) === 'en';
}

type PasoEmbed = { title: string; desc: string };

// Literales de siempre (todo tenant que no es de Brasil, 'en' incluido).
const FINANCING_BENEFICIOS_ES = ['Aprobación rápida', 'Tasas competitivas', 'Plazos flexibles', 'Sin letra chica'];
const FINANCING_REQUISITOS_ES = ['Cédula de identidad', 'Liquidación de sueldo', 'Comprobante de domicilio', 'Antigüedad laboral mínima'];
const CONSIGNMENTS_PASOS_ES: PasoEmbed[] = [
  { title: 'Envía tus datos', desc: 'Completa el formulario con la información de tu vehículo.' },
  { title: 'Evaluación', desc: 'Nuestro equipo evalúa tu vehículo y te contactamos.' },
  { title: 'Publicación', desc: 'Publicamos tu vehículo en múltiples plataformas.' },
  { title: 'Venta', desc: 'Gestionamos todo el proceso hasta cerrar la venta.' },
];
const CONSIGNMENTS_BENEFICIOS_ES = ['Publicación en múltiples plataformas', 'Gestión completa de la venta', 'Mejor precio garantizado'];
const BUYDIRECT_PASOS_TASADOR_ES: PasoEmbed[] = [
  { title: 'Envía tus datos', desc: 'Completa el formulario con la información de tu vehículo.' },
  { title: 'Recibes una estimación', desc: 'Te llega por correo un precio estimado de compra para tu auto.' },
  { title: 'Te hacemos una oferta', desc: 'Coordinamos la tasación presencial y te damos una oferta en firme.' },
  { title: 'Pago inmediato', desc: 'Si aceptas, realizamos el pago de forma inmediata.' },
];
const BUYDIRECT_PASOS_ES: PasoEmbed[] = [
  { title: 'Envía tus datos', desc: 'Completa el formulario con la información de tu vehículo.' },
  { title: 'Evaluamos', desc: 'Nuestro equipo revisa los datos y evalúa tu vehículo.' },
  { title: 'Te hacemos una oferta', desc: 'Recibirás una oferta justa y transparente.' },
  { title: 'Pago inmediato', desc: 'Si aceptas, realizamos el pago de forma inmediata.' },
];
const BUYDIRECT_BENEFICIOS_ES = ['Oferta al instante', 'Pago inmediato', 'Sin comisiones ocultas', 'Trámites incluidos'];
const WESEARCH_PASOS_ES: PasoEmbed[] = [
  { title: 'Cuéntanos qué buscas', desc: 'Completa el formulario con tus preferencias y presupuesto.' },
  { title: 'Buscamos por ti', desc: 'Nuestro equipo busca en toda nuestra red de proveedores.' },
  { title: 'Te mostramos opciones', desc: 'Te presentamos las mejores alternativas encontradas.' },
  { title: 'Compra segura', desc: 'Gestionamos la compra con verificación mecánica incluida.' },
];
const WESEARCH_BENEFICIOS_ES = ['Búsqueda personalizada', 'Acceso a red de proveedores', 'Verificación mecánica incluida', 'Garantía de satisfacción'];

/** Compra directa en modo tasador, Brasil: el 1 y el 4 son los del modo
 *  visitante; el 2 y el 3 son propios del modo (estimación por correo). */
const BUYDIRECT_PASOS_TASADOR_PT: PasoEmbed[] = [
  buyDirectPt.embed.steps[0],
  { title: 'Você recebe uma estimativa', desc: 'Enviamos por e-mail um preço estimado de compra para o seu carro.' },
  { title: 'Fazemos uma proposta', desc: 'Agendamos a avaliação presencial e fazemos uma proposta firme.' },
  buyDirectPt.embed.steps[3],
];

// ── Shared helpers — identical colors as admin form embeds ──
interface FormEmbedProps {
  title?: string;
  subtitle?: string;
  bgColor?: string;
  textColor?: string;
  accentColor?: string;
  variant?: string;
  contactEmail?: string;
  contactPhone?: string;
  showEmail?: boolean;
  showPhone?: boolean;
  formFields?: any[];
  /** Sólo BuyDirectFormEmbed: qué hace con el precio. Ver PrecioModo. */
  precioModo?: PrecioModo;
  /**
   * Sólo ContactFormEmbed: muestra la columna "Información de contacto"
   * (teléfono, correos y horario de la sucursal). Ausente o `true` =
   * exactamente lo de siempre, que es como quedan todos los nodos ya
   * publicados.
   *
   * En `false` la sección queda sólo con el formulario, a todo el ancho. Existe
   * para poder usar el formulario funcional dentro de una composición que ya
   * trae su propio bloque de contacto —y para las automotoras que todavía no
   * tienen el teléfono o el horario cargados, donde esa columna publicaba
   * datos a medias.
   */
  showContactInfo?: boolean;
  /**
   * Textos de la tarjeta informativa, editables desde el Builder (ticket #160):
   * sólo Consignaciones y Compra directa por ahora.
   *
   * Ausentes —todo nodo publicado antes del #160— se dibujan los literales de
   * siempre (o el pt de Brasil), así que la página no cambia hasta que la
   * automotora edita algo. Una lista vacía también cae al respaldo: si quedara
   * en [] el bloque desaparecería sin que nadie lo haya pedido.
   *
   * Vienen del editor como HTML —la edición inline guarda `<b>`, `<br>`…—, así
   * que se pintan con `htmlSeguro`, igual que el resto de las secciones. Y los
   * ítems en blanco no se dibujan: vaciar un beneficio es la forma de sacarlo.
   */
  infoTitle?: string;
  pasos?: Array<{ title?: string; desc?: string }>;
  beneficiosTitle?: string;
  beneficios?: Array<{ text?: string }>;
}

/**
 * La lista que se dibuja: la editada en el Builder, si tiene algo, o la de
 * fábrica. Ticket #160. Los ítems en blanco se descartan, pero si no queda
 * ninguno vuelve el respaldo (nadie pidió que el bloque desaparezca).
 */
function listaEditable<T>(editada: T[] | undefined, deFabrica: T[], vacio: (item: T) => boolean): T[] {
  if (!Array.isArray(editada) || editada.length === 0) return deFabrica;
  const conContenido = editada.filter((item) => !vacio(item));
  return conContenido.length > 0 ? conContenido : deFabrica;
}

const pasoEnBlanco = (p: { title?: string; desc?: string }) =>
  isBlankHtml(p?.title) && isBlankHtml(p?.desc);

function deriveColors(bgColor?: string) {
  const isDark = bgColor && (bgColor.startsWith('#0') || bgColor.startsWith('#1') || bgColor.startsWith('#2'));
  return {
    isDark,
    cardBg: isDark ? '#1c1c1c' : '#ffffff',
    cardBorder: isDark ? '#2a2a2a' : '#e5e7eb',
    infoBg: isDark ? '#1c1c1c' : '#f9fafb',
    subtextColor: isDark ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.45)',
  };
}

// ── Financing ──
const FinancingFormEmbed = ({ bgColor = '#ffffff', textColor = '#111827', accentColor = '#3b82f6', title, subtitle, contactEmail, contactPhone, showEmail = true, showPhone = true }: FormEmbedProps) => {
  const { connectors } = useNode();
  const { cardBg, cardBorder, infoBg, subtextColor } = deriveColors(bgColor);
  const { client } = useClientStore();
  const { t } = useTranslation();
  const br = useEsBrasil();
  const us = useEsUsa();
  const finalEmail = contactEmail || client?.contact?.email || '';
  const finalPhone = contactPhone || client?.contact?.phone || '';
  const showContactBlock = showEmail || showPhone;
  return (
    <div ref={(el: HTMLDivElement | null) => { if (el) connectors.connect(el); }} style={{ backgroundColor: bgColor, color: textColor }} className="w-full py-12 sm:py-16 px-4 sm:px-6 lg:px-8">
      {title && <div className="text-center mb-10 max-w-3xl mx-auto"><h1 className="text-3xl sm:text-4xl font-extrabold" style={{ color: textColor }}>{title}</h1>{subtitle && <p className="mt-4 text-lg" style={{ color: subtextColor }}>{subtitle}</p>}</div>}
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-16">
          <div className="rounded-xl shadow-lg p-8 border" style={{ backgroundColor: cardBg, borderColor: cardBorder }}>
            <FinancingForm embedded bgColor={cardBg} textColor={textColor} accentColor={accentColor} />
          </div>
          <div className="rounded-xl p-8 border" style={{ backgroundColor: infoBg, borderColor: cardBorder }}>
            <h2 className="text-2xl font-bold mb-6" style={{ color: textColor }}>{br ? financingPt.embed.infoTitle : us ? EMBEDS_US.financing.infoTitle : 'Opciones de financiamiento'}</h2>
            <div className="mb-6">
              <h3 className="text-lg font-medium mb-3" style={{ color: textColor }}>{t('site.embeds.benefitsTitle')}</h3>
              <ul className="list-disc pl-5 space-y-2">
                {(br ? financingPt.embed.benefits : us ? EMBEDS_US.financing.benefits : FINANCING_BENEFICIOS_ES).map((item, i) => (
                  <li key={i} className="text-sm" style={{ color: subtextColor }}>{item}</li>
                ))}
              </ul>
            </div>
            <div className="mb-6">
              <h3 className="text-lg font-medium mb-3" style={{ color: textColor }}>{br ? financingPt.embed.requirementsTitle : us ? EMBEDS_US.financing.requirementsTitle : 'Requisitos'}</h3>
              <ul className="list-disc pl-5 space-y-2">
                {(br ? financingPt.embed.requirements : us ? EMBEDS_US.financing.requirements : FINANCING_REQUISITOS_ES).map((item, i) => (
                  <li key={i} className="text-sm" style={{ color: subtextColor }}>{item}</li>
                ))}
              </ul>
            </div>
            {showContactBlock && (
              <div>
                <h3 className="text-lg font-medium mb-3" style={{ color: textColor }}>{br ? financingPt.embed.contactTitle : us ? EMBEDS_US.financing.contactTitle : 'Contacto directo'}</h3>
                <div className="space-y-2">
                  {showEmail && finalEmail && (
                    <p className="text-sm" style={{ color: subtextColor }}>{finalEmail}</p>
                  )}
                  {showPhone && finalPhone && (
                    <p className="text-sm" style={{ color: subtextColor }}>{finalPhone}</p>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ── Consignments ──
const ConsignmentsFormEmbed = ({ bgColor = '#ffffff', textColor = '#111827', accentColor = '#3b82f6', title, subtitle, infoTitle, pasos, beneficiosTitle, beneficios }: FormEmbedProps) => {
  const { connectors } = useNode();
  const { t } = useTranslation();
  const br = useEsBrasil();
  const us = useEsUsa();
  const { cardBg, cardBorder, infoBg, subtextColor } = deriveColors(bgColor);
  // Ticket #160: lo editado en el Builder manda; si no hay nada editado, los
  // textos de siempre (pt para Brasil).
  const finalInfoTitle = infoTitle ?? t('site.embeds.howItWorks');
  const finalBeneficiosTitle = beneficiosTitle ?? t('site.embeds.benefitsTitle');
  const finalPasos = listaEditable(pasos, br ? consignmentsPt.embed.steps : us ? EMBEDS_US.consignments.steps : CONSIGNMENTS_PASOS_ES, pasoEnBlanco);
  const finalBeneficios = listaEditable(
    beneficios,
    (br ? consignmentsPt.embed.benefits : us ? EMBEDS_US.consignments.benefits : CONSIGNMENTS_BENEFICIOS_ES).map((text) => ({ text })),
    (b) => isBlankHtml(b?.text),
  );
  return (
    <div ref={(el: HTMLDivElement | null) => { if (el) connectors.connect(el); }} style={{ backgroundColor: bgColor, color: textColor }} className="w-full py-12 sm:py-16 px-4 sm:px-6 lg:px-8">
      {!isBlankHtml(title) && <div className="text-center mb-10 max-w-3xl mx-auto"><h1 className="text-3xl sm:text-4xl font-extrabold" style={{ color: textColor }} dangerouslySetInnerHTML={htmlSeguro(title || '')} />{!isBlankHtml(subtitle) && <p className="mt-4 text-lg" style={{ color: subtextColor }} dangerouslySetInnerHTML={htmlSeguro(subtitle || '')} />}</div>}
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-16">
          <div className="rounded-xl shadow-lg p-8 border" style={{ backgroundColor: cardBg, borderColor: cardBorder }}>
            <ConsignmentsForm embedded bgColor={cardBg} textColor={textColor} accentColor={accentColor} />
          </div>
          <div className="rounded-xl p-8 border" style={{ backgroundColor: infoBg, borderColor: cardBorder }}>
            {!isBlankHtml(finalInfoTitle) && <h2 className="text-2xl font-bold mb-6" style={{ color: textColor }} dangerouslySetInnerHTML={htmlSeguro(finalInfoTitle)} />}
            <div className="space-y-5 mb-8">
              {finalPasos.map((item, i) => (
                <div key={i} className="flex items-start gap-4">
                  <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0" style={{ backgroundColor: accentColor }}>{i + 1}</div>
                  <div>
                    {!isBlankHtml(item.title) && <p className="text-sm font-medium" style={{ color: textColor }} dangerouslySetInnerHTML={htmlSeguro(item.title || '')} />}
                    {!isBlankHtml(item.desc) && <p className="text-xs mt-0.5" style={{ color: subtextColor }} dangerouslySetInnerHTML={htmlSeguro(item.desc || '')} />}
                  </div>
                </div>
              ))}
            </div>
            <div>
              {!isBlankHtml(finalBeneficiosTitle) && <h3 className="text-lg font-medium mb-3" style={{ color: textColor }} dangerouslySetInnerHTML={htmlSeguro(finalBeneficiosTitle)} />}
              <ul className="list-disc pl-5 space-y-2">
                {finalBeneficios.map((item, i) => (
                  <li key={i} className="text-sm" style={{ color: subtextColor }} dangerouslySetInnerHTML={htmlSeguro(item.text || '')} />
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ── Buy Direct ──
const BuyDirectFormEmbed = ({ bgColor = '#ffffff', textColor = '#111827', accentColor = '#3b82f6', title, subtitle, precioModo = 'visitante', infoTitle, pasos, beneficiosTitle, beneficios }: FormEmbedProps) => {
  const { connectors } = useNode();
  const { t } = useTranslation();
  const br = useEsBrasil();
  const us = useEsUsa();
  const { cardBg, cardBorder, infoBg, subtextColor } = deriveColors(bgColor);
  // Ticket #160. El respaldo de los pasos sigue dependiendo de `precioModo`
  // (ticket #49): sin nada editado, cada modo muestra los suyos.
  const finalInfoTitle = infoTitle ?? t('site.embeds.howItWorks');
  const finalBeneficiosTitle = beneficiosTitle ?? t('site.embeds.benefitsTitle');
  const pasosDeFabrica = precioModo === 'tasador'
    ? (br ? BUYDIRECT_PASOS_TASADOR_PT : us ? EMBEDS_US.buyDirect.stepsTasador : BUYDIRECT_PASOS_TASADOR_ES)
    : (br ? buyDirectPt.embed.steps : us ? EMBEDS_US.buyDirect.steps : BUYDIRECT_PASOS_ES);
  const finalPasos = listaEditable(pasos, pasosDeFabrica, pasoEnBlanco);
  const finalBeneficios = listaEditable(
    beneficios,
    (br ? buyDirectPt.embed.benefits : us ? EMBEDS_US.buyDirect.benefits : BUYDIRECT_BENEFICIOS_ES).map((text) => ({ text })),
    (b) => isBlankHtml(b?.text),
  );
  return (
    <div ref={(el: HTMLDivElement | null) => { if (el) connectors.connect(el); }} style={{ backgroundColor: bgColor, color: textColor }} className="w-full py-12 sm:py-16 px-4 sm:px-6 lg:px-8">
      {!isBlankHtml(title) && <div className="text-center mb-10 max-w-3xl mx-auto"><h1 className="text-3xl sm:text-4xl font-extrabold" style={{ color: textColor }} dangerouslySetInnerHTML={htmlSeguro(title || '')} />{!isBlankHtml(subtitle) && <p className="mt-4 text-lg" style={{ color: subtextColor }} dangerouslySetInnerHTML={htmlSeguro(subtitle || '')} />}</div>}
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-16">
          <div className="rounded-xl shadow-lg p-8 border" style={{ backgroundColor: cardBg, borderColor: cardBorder }}>
            <BuyDirectForm embedded bgColor={cardBg} textColor={textColor} accentColor={accentColor} precioModo={precioModo} />
          </div>
          <div className="rounded-xl p-8 border" style={{ backgroundColor: infoBg, borderColor: cardBorder }}>
            {!isBlankHtml(finalInfoTitle) && <h2 className="text-2xl font-bold mb-6" style={{ color: textColor }} dangerouslySetInnerHTML={htmlSeguro(finalInfoTitle)} />}
            <div className="space-y-5 mb-8">
              {finalPasos.map((item, i) => (
                <div key={i} className="flex items-start gap-4">
                  <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0" style={{ backgroundColor: accentColor }}>{i + 1}</div>
                  <div>
                    {!isBlankHtml(item.title) && <p className="text-sm font-medium" style={{ color: textColor }} dangerouslySetInnerHTML={htmlSeguro(item.title || '')} />}
                    {!isBlankHtml(item.desc) && <p className="text-xs mt-0.5" style={{ color: subtextColor }} dangerouslySetInnerHTML={htmlSeguro(item.desc || '')} />}
                  </div>
                </div>
              ))}
            </div>
            <div>
              {!isBlankHtml(finalBeneficiosTitle) && <h3 className="text-lg font-medium mb-3" style={{ color: textColor }} dangerouslySetInnerHTML={htmlSeguro(finalBeneficiosTitle)} />}
              <ul className="list-disc pl-5 space-y-2">
                {finalBeneficios.map((item, i) => (
                  <li key={i} className="text-sm" style={{ color: subtextColor }} dangerouslySetInnerHTML={htmlSeguro(item.text || '')} />
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ── We Search For You ──
const WeSearchFormEmbed = ({ bgColor = '#ffffff', textColor = '#111827', accentColor = '#3b82f6', title, subtitle, formFields }: FormEmbedProps) => {
  const { connectors } = useNode();
  const { t } = useTranslation();
  const br = useEsBrasil();
  const us = useEsUsa();
  const { cardBg, cardBorder, infoBg, subtextColor } = deriveColors(bgColor);
  return (
    <div ref={(el: HTMLDivElement | null) => { if (el) connectors.connect(el); }} style={{ backgroundColor: bgColor, color: textColor }} className="w-full py-12 sm:py-16 px-4 sm:px-6 lg:px-8">
      {title && <div className="text-center mb-10 max-w-3xl mx-auto"><h1 className="text-3xl sm:text-4xl font-extrabold" style={{ color: textColor }}>{title}</h1>{subtitle && <p className="mt-4 text-lg" style={{ color: subtextColor }}>{subtitle}</p>}</div>}
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-16">
          <div className="rounded-xl shadow-lg p-8 border" style={{ backgroundColor: cardBg, borderColor: cardBorder }}>
            <WeSearchForm embedded bgColor={cardBg} textColor={textColor} accentColor={accentColor} formFields={formFields} />
          </div>
          <div className="rounded-xl p-8 border" style={{ backgroundColor: infoBg, borderColor: cardBorder }}>
            <h2 className="text-2xl font-bold mb-6" style={{ color: textColor }}>{t('site.embeds.howItWorks')}</h2>
            <div className="space-y-5 mb-8">
              {(br ? weSearchForYouPt.embed.steps : us ? EMBEDS_US.weSearch.steps : WESEARCH_PASOS_ES).map((item, i) => (
                <div key={i} className="flex items-start gap-4">
                  <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0" style={{ backgroundColor: accentColor }}>{i + 1}</div>
                  <div><p className="text-sm font-medium" style={{ color: textColor }}>{item.title}</p><p className="text-xs mt-0.5" style={{ color: subtextColor }}>{item.desc}</p></div>
                </div>
              ))}
            </div>
            <div>
              <h3 className="text-lg font-medium mb-3" style={{ color: textColor }}>{t('site.embeds.benefitsTitle')}</h3>
              <ul className="list-disc pl-5 space-y-2">
                {(br ? weSearchForYouPt.embed.benefits : us ? EMBEDS_US.weSearch.benefits : WESEARCH_BENEFICIOS_ES).map((item, i) => (
                  <li key={i} className="text-sm" style={{ color: subtextColor }}>{item}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ── Contact ──
const ContactFormEmbed = ({ bgColor = '#ffffff', textColor = '#111827', accentColor = '#3b82f6', title, subtitle, showContactInfo = true }: FormEmbedProps) => {
  const { connectors } = useNode();
  const { cardBg, cardBorder, infoBg, subtextColor } = deriveColors(bgColor);
  const { client } = useClientStore();
  const { t } = useTranslation();

  // Trae la sucursal para mostrar telefono/email/horario reales desde la
  // plataforma (dealerships), igual que el componente "Como llegar".
  const [dealership, setDealership] = useState<Dealership | null>(null);
  // Ticket #59: el horario sale de Agenda (dealership_hours) por RPC, no del
  // JSONB legacy `dealerships.opening_hours`.
  const [hours, setHours] = useState<PublicDayHours[]>([]);
  useEffect(() => {
    const fetchDealership = async () => {
      // La sucursal y el horario alimentan SÓLO la columna de información: sin
      // ella no hay a quién mostrárselos y la consulta sobra.
      if (!showContactInfo) return;
      if (!client?.id) return;
      // Sucursales y horario: del store si ya vienen con el tenant (los
      // resuelve el layout); si no, de la base como antes.
      let data = sucursalesDelCliente<Dealership>(client);
      if (!data) {
        ({ data } = await supabase.from('dealerships').select<string, Dealership>(DEALERSHIP_PUBLIC_COLS).eq('client_id', client.id));
      }
      if (data && data.length > 0) {
        const principal = data[0] as Dealership;
        setDealership(principal);
        const enStore = horasDelCliente(client);
        const porSucursal = enStore ?? (await getDealershipHours(client.id, Number(principal.id)));
        setHours(porSucursal[Number(principal.id)] || []);
      }
    };
    fetchDealership();
  }, [client?.id, showContactInfo]);

  const contactPhone = dealership?.phone || client?.contact?.phone;
  // Ticket #58: varios correos por sucursal, con fallback al único y al tenant.
  const dealershipEmailList = (
    Array.isArray((dealership as { emails?: string[] | null } | null)?.emails) &&
    (dealership as { emails?: string[] | null }).emails!.length
      ? (dealership as { emails?: string[] | null }).emails!
      : dealership?.email
        ? [dealership.email]
        : []
  )
    .map((e) => String(e).trim())
    .filter(Boolean);
  const contactEmails = dealershipEmailList.length
    ? dealershipEmailList
    : client?.contact?.email
      ? [client.contact.email]
      : [];

  return (
    <div ref={(el: HTMLDivElement | null) => { if (el) connectors.connect(el); }} style={{ backgroundColor: bgColor, color: textColor }} className="w-full py-12 sm:py-16 px-4 sm:px-6 lg:px-8">
      {title && <div className="text-center mb-10 max-w-3xl mx-auto"><h1 className="text-3xl sm:text-4xl font-extrabold" style={{ color: textColor }}>{title}</h1>{subtitle && <p className="mt-4 text-lg" style={{ color: subtextColor }}>{subtitle}</p>}</div>}
      <div className="max-w-7xl mx-auto">
        {/* Sin la columna de información queda UNA sola celda: la grilla se deja
            en una columna para que el formulario ocupe todo el ancho y no sobre
            media sección vacía. Con `showContactInfo` ausente o `true` el
            `md:grid-cols-2` es el de siempre. */}
        <div className={`grid grid-cols-1 gap-16${showContactInfo ? ' md:grid-cols-2' : ''}`}>
          <div className="rounded-xl shadow-lg p-8 border" style={{ backgroundColor: cardBg, borderColor: cardBorder }}>
            <ContactForm embedded bgColor={cardBg} textColor={textColor} accentColor={accentColor} />
          </div>
          {showContactInfo && (
            <div className="rounded-xl p-8 border" style={{ backgroundColor: infoBg, borderColor: cardBorder }}>
              <h2 className="text-2xl font-bold mb-6" style={{ color: textColor }}>{t('site.embeds.contactInfo')}</h2>
              <div className="space-y-6">
                {contactPhone && <div><h3 className="text-lg font-medium mb-2" style={{ color: textColor }}>{t('site.embeds.phone')}</h3><p className="text-sm" style={{ color: subtextColor }}>{contactPhone}</p></div>}
                {contactEmails.length > 0 && <div><h3 className="text-lg font-medium mb-2" style={{ color: textColor }}>{contactEmails.length > 1 ? t('site.embeds.emails') : t('site.embeds.email')}</h3>{contactEmails.map((e, i) => (<p key={i} className="text-sm" style={{ color: subtextColor }}>{e}</p>))}</div>}
                {hours.length > 0 && <div><h3 className="text-lg font-medium mb-2" style={{ color: textColor }}>{t('site.embeds.openingHours')}</h3><OpeningHours hours={hours} labelColor={subtextColor} valueColor={subtextColor} /></div>}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ── Agendar visita ──
const AgendarFormEmbed = ({ bgColor = '#ffffff', textColor = '#111827', accentColor = '#3b82f6', title, subtitle }: FormEmbedProps) => {
  const { connectors } = useNode();
  const { cardBg, cardBorder, subtextColor } = deriveColors(bgColor);
  const { client } = useClientStore();
  const { t } = useTranslation();
  return (
    <div ref={(el: HTMLDivElement | null) => { if (el) connectors.connect(el); }} style={{ backgroundColor: bgColor, color: textColor }} className="w-full py-12 sm:py-16 px-4 sm:px-6 lg:px-8">
      {title && <div className="text-center mb-10 max-w-3xl mx-auto"><h1 className="text-3xl sm:text-4xl font-extrabold" style={{ color: textColor }}>{title}</h1>{subtitle && <p className="mt-4 text-lg" style={{ color: subtextColor }}>{subtitle}</p>}</div>}
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-16">
          <div className="rounded-xl shadow-lg p-8 border" style={{ backgroundColor: cardBg, borderColor: cardBorder }}>
            <AgendarForm embedded client={client ?? undefined} bgColor={cardBg} textColor={textColor} accentColor={accentColor} />
          </div>
          <div className="md:pt-2">
            <h2 className="text-2xl font-bold mb-6" style={{ color: textColor }}>{t('site.embeds.howItWorks')}</h2>
            <div className="space-y-5">
              {[
                { title: t('site.embeds.agendar.step1Title'), desc: t('site.embeds.agendar.step1Desc') },
                { title: t('site.embeds.agendar.step2Title'), desc: t('site.embeds.agendar.step2Desc') },
                { title: t('site.embeds.agendar.step3Title'), desc: t('site.embeds.agendar.step3Desc') },
                { title: t('site.embeds.agendar.step4Title'), desc: t('site.embeds.agendar.step4Desc') },
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-4">
                  <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0" style={{ backgroundColor: accentColor }}>{i + 1}</div>
                  <div><p className="text-sm font-medium" style={{ color: textColor }}>{item.title}</p><p className="text-xs mt-0.5" style={{ color: subtextColor }}>{item.desc}</p></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ── About ──
interface AboutProps extends FormEmbedProps {
  values?: { title: string; description: string; emoji: string }[];
  teamTitle?: string;
  teamSubtitle?: string;
  members?: { name: string; role: string; imageUrl?: string; phone?: string; email?: string }[];
  showTeam?: boolean;
}
// Fila de 3 que se centra: con 1 o 2 tarjetas ya no quedan pegadas a la
// izquierda con un hueco a la derecha.
const ABOUT_ITEM_CLASS = autoFitItem([{ cols: 1 }, { bp: 'md', cols: 3 }]);

const AboutContentEmbed = ({
  bgColor = '#ffffff', textColor = '#111827', accentColor = '#3b82f6', title, subtitle,
  values = [], teamTitle: teamTitleProp, teamSubtitle = '', members = [], showTeam = true,
}: AboutProps) => {
  const { connectors } = useNode();
  const { t } = useTranslation();
  // Igual que el default de antes: solo cuando la prop no vino.
  const teamTitle = teamTitleProp === undefined ? t('site.embeds.teamTitle') : teamTitleProp;
  const { cardBg, cardBorder, subtextColor } = deriveColors(bgColor);
  const avatarBg = bgColor && (bgColor.startsWith('#0') || bgColor.startsWith('#1') || bgColor.startsWith('#2')) ? '#262626' : '#e5e7eb';
  return (
    <div ref={(el: HTMLDivElement | null) => { if (el) connectors.connect(el); }} style={{ backgroundColor: bgColor, color: textColor }} className="w-full py-12 sm:py-16 px-4 sm:px-6 lg:px-8">
      {title && <div className="text-center mb-10 max-w-3xl mx-auto"><h1 className="text-3xl sm:text-4xl font-extrabold" style={{ color: textColor }}>{title}</h1>{subtitle && <p className="mt-4 text-lg" style={{ color: subtextColor }}>{subtitle}</p>}</div>}
      <div className="max-w-7xl mx-auto">
        {values.length > 0 && (
          <div className={`${AUTO_FIT_ROW} gap-8 [--bg-gap:2rem] mb-16`}>
            {values.map((card, i) => (
              <div key={i} className={`${ABOUT_ITEM_CLASS} rounded-xl shadow-lg p-8 border text-center`} style={{ backgroundColor: cardBg, borderColor: cardBorder }}>
                <div className="w-14 h-14 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ backgroundColor: `${accentColor}15` }}>
                  <span className="text-2xl">{card.emoji || '⭐'}</span>
                </div>
                <h3 className="text-xl font-bold mb-3" style={{ color: textColor }}>{card.title}</h3>
                <p className="text-sm leading-relaxed" style={{ color: subtextColor }}>{card.description}</p>
              </div>
            ))}
          </div>
        )}
        {showTeam && members.length > 0 && (
          <>
            <div className="text-center mb-10"><h2 className="text-2xl font-bold mb-2" style={{ color: textColor }}>{teamTitle}</h2>{teamSubtitle && <p className="text-sm" style={{ color: subtextColor }}>{teamSubtitle}</p>}</div>
            <div className={`${AUTO_FIT_ROW} gap-8 [--bg-gap:2rem]`}>
              {members.map((member, i) => (
                <div key={i} className={`${ABOUT_ITEM_CLASS} rounded-xl shadow-lg p-8 border flex flex-col items-center`} style={{ backgroundColor: cardBg, borderColor: cardBorder }}>
                  {member.imageUrl ? (
                    <img src={member.imageUrl} alt={member.name} className="w-24 h-24 rounded-full mb-4 object-cover" />
                  ) : (
                    <div className="w-24 h-24 rounded-full mb-4" style={{ backgroundColor: avatarBg }} />
                  )}
                  <h3 className="text-lg font-bold" style={{ color: textColor }}>{member.name}</h3>
                  <p className="text-sm mt-1" style={{ color: subtextColor }}>{member.role}</p>
                  {/* Contacto del integrante: opcional, desaparece si no cargaron ninguno. */}
                  {(member.phone || member.email) && (
                    <div className="mt-3 flex flex-col items-center gap-1 text-sm">
                      {member.phone && (
                        <a href={`tel:${member.phone.replace(/[^\d+]/g, '')}`} className="hover:underline" style={{ color: accentColor }}>{member.phone}</a>
                      )}
                      {member.email && (
                        <a href={`mailto:${member.email}`} className="hover:underline break-all" style={{ color: accentColor }}>{member.email}</a>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// =====================
// Fallback seguro
// =====================
const Unknown: React.FC<React.PropsWithChildren<{}>> = ({ children }) => (
  <div style={{ display: 'contents' }}>{children}</div>
);
// @ts-ignore
(Unknown as any).craft = {
  displayName: 'Unknown',
  isCanvas: true,
  rules: {},
  props: {},
};

// =====================
// Resolver con Proxy
// =====================
export const baseResolver: Record<string, any> = {
  Container, Section, Grid, Text, Image, Button, Heading, Divider, Spacer, Icon, SocialLinks,
  VideoPlayer, ImageText, Card, ListBlock, MapEmbed,
  GalleryBlock, Carousel, AccordionBlock, IconText, WhatsAppButton,
  HeroBasic, HeroWithImage, HeroWithBackground, HeroWithCard, HeroCarSearch,
  HeroFeatureCards, HeroTestimonial, HeroSearchBanner, HeroImageDivided,
  HeroWithLogo, HeroWelcome, HeroMinimalistic, HeroWithVideoEmbed, HeroMega,
  VehicleGrid, VehicleGrid2, VehicleCarousel, TraditionalVehicleGrid,
  WhyChooseUs, FAQ, Testimonials, TraditionalWhyUs,
  ContactCTA, TraditionalContactCTA: BuilderTraditionalContactCTA,
  HowToArrive, TraditionalHowToArrive, FloatingWhatsApp,
  VideoEmbed,
  HeroModerno, StatsModerno, TestimonialsModerno, CTAModerno, FooterModerno,
  HeroPremium, FeatureShowcase, TestimonialsPremium, GalleryPremium, CTAPremium,
  BuilderNavbar, NavbarSimple, Footer, StatsCounter, PromoBanner, AwardsBadges, TrustBadges, LogoCloud, LogoMarquee, LogoGrid, PhotoGallery, ImageCarousel, TeamMembers,
  FinancingFormEmbed, ConsignmentsFormEmbed, BuyDirectFormEmbed, WeSearchFormEmbed, ContactFormEmbed, AboutContentEmbed, AgendarFormEmbed,
  EmbedCodeBlock,
  // Piezas internas de las secciones (título, botón…): ver utils/piezaLibre.
  PiezaLibre,
  div: Unknown, p: Unknown, span: Unknown, img: Unknown, Unknown,
};

const resolver = new Proxy(baseResolver, {
  get(target, prop: string) {
    return prop in target ? (target as any)[prop] : Unknown;
  },
});

// =====================
// Error Boundary para Builder
// =====================
class BuilderErrorBoundary extends React.Component<
  { children: React.ReactNode; fallback: React.ReactNode },
  { hasError: boolean }
> {
  constructor(props: any) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError(error: Error) {
    console.error('[BuilderErrorBoundary] CAUGHT ERROR:', error);
    return { hasError: true };
  }
  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error('[BuilderErrorBoundary] Error details:', error, info);

    // Alto valor: este boundary se TRAGA el crash de cada sección del builder y
    // muestra un fallback, así que hasta ahora una sección rota de un tenant era
    // completamente invisible. Es además el único reporte posible: React 19 no
    // manda a window.onerror lo que un boundary atrapa.
    captureException(error, {
      source: 'builder-section-boundary',
      component_stack: info.componentStack,
    });
  }
  render() {
    if (this.state.hasError) return this.props.fallback;
    return this.props.children;
  }
}

// =====================
// BuilderRenderer
// =====================
export default function BuilderRenderer({
  data,
  themeKey = 'default',
  fallback = null,
}: {
  data: any;
  themeKey?: string;
  fallback?: React.ReactNode;
}) {
  // Brasil: los textos de fábrica del builder (los que la automotora no editó)
  // en portugués. Resto de los tenants: el árbol tal cual, la MISMA referencia.
  const esBrasil = useEsBrasil();
  const datosBr = useMemo(
    () => (esBrasil ? traducirDefaultsDelArbolPt(data, baseResolver) : data),
    [data, esBrasil]
  );
  // EE.UU.: las props de texto que el árbol NO trae (Craft las llenaría con los
  // defaults en español de `craft.props`) se completan en inglés. Lo guardado no
  // se toca. Resto de los tenants: la MISMA referencia de arriba.
  const esUsa = useEsUsa();
  const datos = useMemo(
    () => (esUsa ? completarDefaultsDelArbolEn(datosBr, baseResolver) : datosBr),
    [datosBr, esUsa]
  );
  return (
    <div className="min-h-screen">
      <BuilderErrorBoundary fallback={fallback || <div />}>
        <Editor key={themeKey} resolver={resolver} enabled={false} onRender={RenderNodoLibre}>
          <Frame data={datos} />
        </Editor>
      </BuilderErrorBoundary>
    </div>
  );
}
